import { useQuery } from '@tanstack/react-query';
import { Bell, Calendar, House, Inbox, MessageCircle, SlidersHorizontal, Users, type LucideIcon } from 'lucide-react';
import { leadsQuery, meQuery } from '../lib/queries';

export type NavPath = '/dashboard' | '/leads' | '/bookings' | '/guides' | '/chats' | '/needs-you' | '/settings';

export interface NavItem {
  to: NavPath;
  label: string;
  icon: LucideIcon;
  /** Which paths light this item up. Calendar lives under Bookings; on the phone Guides does too. */
  match: (path: string) => boolean;
  /** Which count rides in the coral badge. */
  badge?: 'needs' | 'leads';
}

const DASHBOARD: NavItem = { to: '/dashboard', label: 'Dashboard', icon: House, match: (p) => p === '/' || p.startsWith('/dashboard') };
const LEADS: NavItem = { to: '/leads', label: 'Leads', icon: Inbox, match: (p) => p.startsWith('/leads'), badge: 'leads' };
const BOOKINGS: NavItem = { to: '/bookings', label: 'Bookings', icon: Calendar, match: (p) => p.startsWith('/bookings') || p.startsWith('/calendar') };
const GUIDES: NavItem = { to: '/guides', label: 'Guides', icon: Users, match: (p) => p.startsWith('/guides') };
/** The phone has no room for Guides in the tab bar: it opens from the Bookings switch. */
const BOOKINGS_PHONE: NavItem = { ...BOOKINGS, match: (p) => BOOKINGS.match(p) || p.startsWith('/guides') };
const CHATS: NavItem = { to: '/chats', label: 'Chats', icon: MessageCircle, match: (p) => p.startsWith('/chats') };
const NEEDS_YOU: NavItem = { to: '/needs-you', label: 'Needs You', icon: Bell, match: (p) => p.startsWith('/needs-you'), badge: 'needs' };
const SETTINGS: NavItem = { to: '/settings', label: 'Settings', icon: SlidersHorizontal, match: (p) => p.startsWith('/settings') };

/**
 * The desktop rail, top to bottom, in two groups with a thin divider between them. Needs You
 * sits right above Settings: it is Seth's inbox of decisions.
 */
export const RAIL_NAV: NavItem[][] = [
  [DASHBOARD, LEADS, BOOKINGS, GUIDES, CHATS],
  [NEEDS_YOU, SETTINGS],
];

/** The phone tab bar: five places. Settings opens from the Dashboard header. */
export const PHONE_NAV: NavItem[] = [DASHBOARD, LEADS, BOOKINGS_PHONE, CHATS, NEEDS_YOU];

/**
 * The badge counts: decisions waiting in Needs You, and leads waiting on Seth or new since he
 * looked. A server that cannot answer /api/leads yet only loses the Leads badge, nothing else.
 */
export function useNavBadges(): Record<NonNullable<NavItem['badge']>, number> {
  const { data: me } = useQuery(meQuery);
  const { data: leads } = useQuery(leadsQuery);
  const counts = leads?.counts;
  return {
    needs: me?.needsYouCount ?? 0,
    leads: (counts?.needsYou ?? 0) + (counts?.new ?? 0),
  };
}
